import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { formatDateTime } from '@/utils/common';
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    DownloadOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, message, Row, Select, Table, TableColumnsType, Tabs, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getHistoryDish,
    importFile,
    watchListHistory,
    watchDishPaginationHistory,
    watchListDishAge,
    resetMessages,
    watchDishMessage,
    getListDishAge,
    downloadTemplateQuantitative
} from '@/redux/slices/dish.slice';
import { Form } from 'antd';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import { setMessages } from '@/redux/slices/app.slice';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { handleDownload } from '@/utils/common';

type PageChangeParams = {
    page?: number;
    pageSize?: number;
};

export default function DishByGroupTable() {
    const [activeTab, setActiveTab] = useState<string>('INPUT');
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const [file, setFile] = useState<File | null>(null);
    const listDishAge = useAppSelector(watchListDishAge);
    const { push } = useQuery();
    const [fileName, setFileName] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const messageInfo = useAppSelector(watchDishMessage);
    const [age, setAge] = useState<string>();
    const listHistory = useAppSelector(watchListHistory);
    const pagination = useAppSelector(watchDishPaginationHistory);

    const ageDishGroupOptions = listDishAge.map((item) => ({
        value: item.id,
        label: item.name
    }));

    const handlePageChange = useCallback(
        ({
            page = PAGINATION_PARAMS.offset,
            pageSize = PAGINATION_PARAMS.limit,
        }: PageChangeParams) => {
            dispatch(getHistoryDish({
                offset: page,
                limit: pageSize
            }));
        },
        [dispatch]
    );

    const handleUpload = async () => {
        if (!selectedFile || !age) {
            message.warning('Vui lòng chọn đầy đủ thông tin và file để tải lên.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('age_group_id', age);
        try {
            await dispatch(importFile(formData)).unwrap();
            push(PageURLs.ofDishManagement());
        } catch (error: any) {
            console.error(error)
        }
    };

    const handleDelFile = () => {
        setFile(null);
    }

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            const MAX_SIZE = 100 * 1024 * 1024; // 100MB
            if (file.size > MAX_SIZE) {
                message.error('Dung lượng file không được vượt quá 100MB');
                return;
            }
            setFileName(file.name);
            setSelectedFile(file);
        }
    };
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const MAX_SIZE = 100 * 1024 * 1024; // 100MB
            if (file.size > MAX_SIZE) {
                message.error('Dung lượng file không được vượt quá 100MB');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }
            if (!file.name.endsWith('.zip')) {
                message.error('Vui lòng chọn file .zip');
                return;
            }
            setFileName(file.name);
            setSelectedFile(file);
        }
    };

    const handleGoBack = useCallback(() => {
        push(PageURLs.ofDishManagement());
    }, [push]);

    const tabItems = [
        {
            key: 'INPUT',
            label: 'Nhập dữ liệu'
        },
        {
            key: 'HISTORY',
            label: 'Lịch sử nhập dữ liệu'
        }
    ];

    useEffect(() => {
        dispatch(getHistoryDish({}));
        dispatch(getListDishAge());
    }, [dispatch]);

    useEffect(() => {
        if (messageInfo) {
            dispatch(
                setMessages([
                    {
                        type: messageInfo.type,
                        message: messageInfo.message,
                        description: messageInfo.description
                    }
                ])
            );

            if (messageInfo.type === 'success') {
                handleCancel();
            }
            dispatch(resetMessages());
        }
    }, [dispatch, messageInfo]);

    const handleCancel = () => {
        setFileName(null);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDownLoadTemplateDish = async () => {
        const template = await dispatch(downloadTemplateQuantitative()).unwrap();
        if (template) {
            handleDownload(template.URL, template.NAME);
        }
    }

    const columns: TableColumnsType<any> = [
        {
            title: 'STT',
            render: (_, __, index) => index + 1,
            width: 100
        },
        {
            title: 'Tên file',
            dataIndex: 'fileName',
            key: 'fileName',
            width: 400
        },
        {
            title: 'Tải xuống',
            dataIndex: 'fileUrl',
            key: 'fileUrl',
            render: (url: string, record: any) => {
                return (
                    <div
                        css={css`
                display: flex;
                justify-content: center;
                align-items: center;

                .ant-btn {
                    border: none;
                }
            `}
                    >
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(url, record.fileName)}
                        />
                    </div>
                );
            },
            width: 100
        },
        {
            title: 'Ngày nhập dữ liệu',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
            render: (record) => (record ? formatDateTime(record) : '')
        },
        {
            title: 'Người nhập',
            dataIndex: 'adminName',
            key: 'adminName',
            width: 200
        }
    ];

    return (
        <>
            <Form form={form} css={rootStyles}>
                <div css={rootStyles}>
                    <Row css={boxStyles} align='middle' gutter={16}>
                        <Button
                            type='text'
                            icon={<ArrowLeftOutlined />}
                            onClick={handleGoBack}
                        ></Button>
                        <Typography.Title level={5} style={{ marginBottom: 0 }}>
                            Nhập món ăn theo danh sách
                        </Typography.Title>
                    </Row>

                    <Tabs
                        css={tabStyle}
                        defaultActiveKey='INPUT'
                        onChange={(key) => setActiveTab(key)}
                    >
                        {tabItems.map((item) => (
                            <Tabs.TabPane tab={item.label} key={item.key} />
                        ))}
                    </Tabs>

                    {activeTab === 'INPUT' && (
                        <>
                            <div css={boxStyles}>
                                <Row>
                                    <Button type='primary' icon={<UploadOutlined />}
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={!age}
                                    >
                                        Tải lên
                                    </Button>
                                    <Button type='link'
                                        onClick={() => handleDownLoadTemplateDish()}
                                    >
                                        Tải xuống template
                                    </Button>
                                </Row>
                                <Row>
                                    <Typography.Text
                                    >
                                        {file?.name}
                                    </Typography.Text>
                                    <DeleteOutlined
                                        hidden={!file}
                                        style={{ fontSize: 23 }}
                                        onClick={() => handleDelFile()}
                                    />
                                </Row>
                            </div>
                            <div css={boxStyles}>
                                <Typography.Text type="secondary" style={{ marginBottom: 4, display: 'block' }}>
                                    (Vui lòng chọn độ tuổi để nhập danh sách món ăn theo độ tuổi tương ứng.)
                                </Typography.Text>
                                <Form.Item
                                    name="age"
                                    label="Độ tuổi"
                                    rules={[{ required: true, message: 'Vui lòng chọn độ tuổi!' }]}
                                >
                                    <Select
                                        placeholder='Chọn độ tuổi'
                                        options={ageDishGroupOptions}
                                        onChange={(value) => setAge(value)}
                                        allowClear
                                    />
                                </Form.Item>
                            </div>

                            <div css={boxStyles}>
                                <label
                                    htmlFor="file-upload"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={handleDrop}
                                >
                                    <div
                                        style={{
                                            minHeight: '60vh',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Typography.Title level={5}>
                                            {fileName ? `Đã chọn: ${fileName}` : 'Vui lòng kéo thả hoặc chọn thư mục dữ liệu danh sách món ăn'}
                                        </Typography.Title>
                                    </div>
                                    <input
                                        id="file-upload"
                                        type="file"
                                        accept=".zip"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                    />
                                </label>
                            </div>
                            <div css={styleFooter}>
                                <Row align='top' gutter={16}>
                                    <Button
                                        type='default'
                                        size='large'
                                        onClick={handleGoBack}
                                    >
                                        Hủy
                                    </Button>

                                    <Form.Item noStyle>
                                        <Button type='primary'
                                            size='large'
                                            onClick={handleUpload}
                                            style={{ marginLeft: 20 }}
                                        >
                                            Tải lên
                                        </Button>
                                    </Form.Item>
                                </Row>
                            </div>
                        </>
                    )}
                    {activeTab === 'HISTORY' && (
                        <Table<any>
                            columns={columns}
                            dataSource={listHistory}
                            pagination={{
                                showQuickJumper: true,
                                showSizeChanger: true,
                                current: pagination.curPage,
                                pageSize: pagination.limitPage,
                                total: pagination.totalRows,
                                onChange: (page, pageSize) => {
                                    handlePageChange({ page, pageSize });
                                }
                            }}
                        />
                    )}
                </div>
            </Form>
        </>
    );
}

const styleFooter = css`
  min-height: calc(12vh - var(--header-height));
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 0 1rem;
  background-color: var(--white-color);
  .ant-btn-lg {
    width: 30rem;
  }
`;

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);

    .ant-tabs-tab-btn {
      color: #ffffff !important;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-tab:hover {
    color: #333 !important;
  }

  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }
`;
